import { joiResolver } from '@hookform/resolvers/joi';
import { Button } from 'components/atoms/Button';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import { Select } from 'components/atoms/Form/Select';
import SimpleAlertV2 from 'components/atoms/SimpleAlertV2.component';
import dayjs from 'dayjs';
import Joi from 'joi';
import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  StandingOrderFrequency,
  type Connector,
} from 'types/transactions.types';
import { isTaxReserveAccountableBank, TransferFieldNames } from './helpers';
import type { TransferSchedule } from './MoneyTransfer';

type FormData = {
  [TransferFieldNames.period]: StandingOrderFrequency;
  [TransferFieldNames.firstExecutionDate]: Date;
  [TransferFieldNames.lastExecutionDate]?: Date;
};

const TransferScheduleForm: FC<{
  sourceAccount?: Connector;
  defaultValues: TransferSchedule;
  onSubmit: (values: TransferSchedule) => void;
}> = ({ defaultValues, onSubmit, sourceAccount }) => {
  const { t } = useTranslation();

  const fromTaxReserveAccount = isTaxReserveAccountableBank(sourceAccount);

  // Convert TransferSchedule to FormData
  const formDefaultValues: FormData = {
    [TransferFieldNames.period]:
      defaultValues.period || StandingOrderFrequency.OneTime,
    [TransferFieldNames.firstExecutionDate]:
      defaultValues.firstExecutionDate || new Date(),
    [TransferFieldNames.lastExecutionDate]: defaultValues.lastExecutionDate,
  };

  const formAPI = useForm<FormData>({
    defaultValues: formDefaultValues,
    resolver: joiResolver(
      Joi.object({
        [TransferFieldNames.period]: Joi.string().required(),
        [TransferFieldNames.firstExecutionDate]: Joi.date()
          .required()
          .min(dayjs().startOf('day').toDate())
          .custom((value, helpers) => {
            const period =
              helpers.state.ancestors[0][TransferFieldNames.period];

            if (
              period === StandingOrderFrequency.Monthly &&
              dayjs(value).date() > 28
            ) {
              return helpers.error('date.maxDay');
            }
            return value;
          })
          .when(TransferFieldNames.period, {
            is: StandingOrderFrequency.OneTime,
            then: Joi.date().max(dayjs().add(1, 'year').toDate()),
          })
          .messages({
            'date.min': t(
              'accountable_banking.scheduled_payments.schedule_form.date.future',
              {
                label: t(
                  'accountable_banking.scheduled_payments.schedule_form.start_date',
                ),
              },
            ),
            'date.max': t(
              'accountable_banking.scheduled_payments.schedule_form.date.max_year',
            ),
            'date.maxDay': t(
              'accountable_banking.scheduled_payments.schedule_form.date.max_day',
            ),
          }),
        [TransferFieldNames.lastExecutionDate]: Joi.date()
          .optional()
          .min(Joi.ref(TransferFieldNames.firstExecutionDate))
          .messages({
            'date.min': t(
              'accountable_banking.scheduled_payments.schedule_form.date.min2',
            ),
          }),
      }),
    ),
  });

  const periodValue = formAPI.watch(
    TransferFieldNames.period as keyof FormData,
  ) as StandingOrderFrequency;

  const handleSubmit = (formData: FormData) => {
    // Convert FormData back to TransferSchedule
    const scheduleData: TransferSchedule = {
      period: formData[TransferFieldNames.period],
      firstExecutionDate: formData[TransferFieldNames.firstExecutionDate],
      lastExecutionDate: formData[TransferFieldNames.lastExecutionDate],
    };
    onSubmit(scheduleData);
  };

  return (
    <FormProvider {...formAPI}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          formAPI.handleSubmit(handleSubmit)(e);
        }}
      >
        <div>
          <div className="flex items-center justify-center mb-5">
            <p className="font-semibold">
              {t('accountable_banking.scheduled_payments.schedule_form.when')}
            </p>
          </div>

          <FieldConnector
            name={TransferFieldNames.period}
            label={t('frequency')}
          >
            {({ field: { onChange, value, ...rest } }) => (
              <Select
                options={[
                  {
                    label: t('OneTime'),
                    value: StandingOrderFrequency.OneTime,
                  },
                  {
                    label: t('Monthly'),
                    value: StandingOrderFrequency.Monthly,
                  },
                  {
                    label: t('Weekly'),
                    value: StandingOrderFrequency.Weekly,
                  },
                  {
                    label: t('Daily'),
                    value: StandingOrderFrequency.Daily,
                  },
                ]}
                value={value}
                onChange={(option) => {
                  onChange(option.value);
                }}
                {...rest}
              />
            )}
          </FieldConnector>

          <div className="flex items-start space-x-4 my-3">
            <FieldConnector
              className="flex-1 min-w-0"
              label={t(
                'accountable_banking.scheduled_payments.schedule_form.start_date',
              )}
              name={TransferFieldNames.firstExecutionDate}
            >
              {({ field }) => (
                <DatePicker
                  {...field}
                  selected={field.value}
                  minDate={dayjs().startOf('day').toDate()}
                  onChange={(date) =>
                    field.onChange(dayjs(date).format('YYYY-MM-DD'))
                  }
                  placeholder={dayjs().format('YYYY-MM-DD')}
                />
              )}
            </FieldConnector>

            <FieldConnector
              className="flex-1 min-w-0"
              label={t(
                'accountable_banking.scheduled_payments.schedule_form.end_date',
              )}
              name={TransferFieldNames.lastExecutionDate}
              showField={periodValue !== StandingOrderFrequency.OneTime}
            >
              {({ field }) => (
                <DatePicker
                  {...field}
                  selected={field.value}
                  onChange={(date) =>
                    field.onChange(dayjs(date).format('YYYY-MM-DD'))
                  }
                />
              )}
            </FieldConnector>
          </div>

          {fromTaxReserveAccount ? (
            <SimpleAlertV2
              subtitle={t(
                'accountable_banking.scheduled_payments.schedule_form.unsupported_payment_alert.content',
              )}
              type="warning"
            />
          ) : null}

          <Button
            type="submit"
            className="w-full mt-6"
            disabled={fromTaxReserveAccount}
          >
            {t('continue')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default TransferScheduleForm;
